import { Component, Input, ChangeDetectorRef, OnInit, NgZone } from '@angular/core';
import { AppService } from '../app.service';
import { SessionService } from '../shared/session.service';
import { UtilsService } from '../shared/utils.service';
import { Observable } from 'rxjs';
import {
  Breakpoints,
  BreakpointObserver,
  MediaMatcher
} from '@angular/cdk/layout';
import { map } from 'rxjs/operators';
import { RouterService } from '../shared/router.service';
import { AccountModel } from '../models/account.model';
import { ManagedAccountService } from './managed-account.service';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { UserMessagesService } from '../user/user-messages/user-messages.service';
import {AuthService} from "../shared/auth.service";
import { AppcuesService } from '../services/appcues.service';
import { HubspotService } from '../services/hubspot.service';
import {SentryService} from "../services/sentry.service";
import {SentryErrorTesterComponent} from "../shared/sentry-error-tester/sentry-error-tester.component";
import { BackgroundExportService } from '../shared/background-export.service';

@Component({
  selector: 'app-top-nav',
  templateUrl: './top-nav.component.html',
  styleUrls: ['./top-nav.component.scss']
})
export class TopNavComponent {
  @Input() sidenav: any;
  mobileQuery: MediaQueryList;
  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(map((result) => result.matches));
  filteredAccounts: AccountModel[];
  accountSearch: string;

  private readonly _mobileQueryListener: () => void;

  // True while the HubSpot chat widget is loading/opening, used to show a spinner
  // on the chat button.
  chatOpening: boolean = false;

  constructor(
    public sessionService: SessionService,
    public app: AppService,
    public utils: UtilsService,
    public routerService: RouterService,
    private auth: AuthService,
    private breakpointObserver: BreakpointObserver,
    private managedAccountService: ManagedAccountService,
    media: MediaMatcher,
    changeDetectorRef: ChangeDetectorRef,
    private zone: NgZone,
    private router: Router,
    private location: Location,
    public userMessagesService: UserMessagesService, // Used to display unread messages count
    private appcuesService: AppcuesService,
    public hubspot: HubspotService,
    public sentry: SentryService,
    public backgroundExports: BackgroundExportService
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 1200px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  getAccountName() {
    return this.app.account.name +
    (this.app.isAuthenticated && this.mobileQuery.matches
      ? ' (' + this.app.account.subscription + ')'
      : '');
  }

  /**
   * Opens the HubSpot live chat from the top navigation button (custom launcher).
   * Shows a spinner on the button while the widget loads/opens. A safety timeout
   * clears the spinner in case the widget never signals that it opened.
   *
   * @return {void} Does not return a value.
   */
  onOpenChat(): void {
    if ( this.chatOpening ) {
      return;
    }
    this.chatOpening = true;

    // HubSpot callbacks run outside Angular's zone, so reset inside the zone to
    // ensure change detection updates the button.
    const reset = (): void => this.zone.run((): void => { this.chatOpening = false; });
    const safetyTimeout = setTimeout(reset, 15000);

    this.hubspot.openChat().finally((): void => {
      clearTimeout(safetyTimeout);
      reset();
    });
  }

  /**
   * Signs the user out of their current session.
   *
   * @return {void} No return value.
   */
  onSignOut(): void {
    this.auth.signOut();
  }

  onSearchAccounts() {
    this.managedAccountService
      .query({ search: this.accountSearch, order: 'asc' })
      .subscribe((response) => {
        this.filteredAccounts = response.data;
      });
  }

  openAccountSwitcher() {
    this.managedAccountService.query({ order: 'asc' }).subscribe((response) => {
      const excludeMe = response.data.filter(
        (account) => account.id !== this.app.account.id
      );
      this.filteredAccounts = excludeMe;
    });
  }

  switchAccount(id: number) {
    this.managedAccountService.switch(id).subscribe((response) => {
      this.app.account.apply(response.data.account);
      this.app.userAccountLink.apply(response.data.user_account_link);

      this.sentry.setActiveAccount(this.app.account.id);

      this.appcuesService.onIdentifyGroup(this.app.account.name, {
        name: this.app.account.name,
        role: this.app.userAccountLink.permissions,
        industry: this.app.account.industry,
        address: this.app.account.address,
        suburb: this.app.account.suburb,
        city: this.app.account.city,
        postal_code: this.app.account.postal_code,
        country: this.app.account.country
      });

      // window.location.reload();
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(this.location.path());
      });
    });
  }

  onSentryErrorTester(){
    this.utils.showComponentDialog(
      SentryErrorTesterComponent,
      {},
      {
        width: '1024px'
      },
      (results) => {
        if ( typeof results !== 'undefined' ) {
          //
        }
      }
    );
  }

  /**
   * Stop every active background-export polling loop. The exports still
   * complete on the server (the queue job is independent of the browser);
   * the user just stops watching for them. The email and the My Exports
   * list remain as fallback delivery mechanisms.
   */
  onCancelAllBackgroundExports(): void {
    this.utils.showModal(
      'Stop polling for exports?',
      'The exports will still complete on the server and appear on the My Exports page. You will also receive an email for each one when it is ready.',
      () => {
        this.backgroundExports.cancelAll();
        this.utils.showToast('Stopped polling for all in-flight exports.');
      }
    );
  }

}
